import { Lock } from "lucide-react";
import { useState } from "react";
import { Link, useLocation } from "react-router";
import { useDocs } from "../app/docs-context";
import { startHandoff } from "../app/handoff";
import { ApiError } from "../shared/api/client";
import { bffApi } from "../shared/api/endpoints";
import { READER_ROLE_LABELS, ReaderRole } from "../shared/api/types";
import { SignInForm } from "./signin";

/**
 * Shown when an article exists but this reader may not open it.
 *
 * Names the document so the reader knows what is behind the gate, and nothing else about it — the
 * host sends only the title and the rung with its 403 for exactly this screen.
 *
 * Who can get past it here differs by rung (ADR-0046 decision 4):
 *
 * - A customer rung is the product's to assert. A signed-in customer reads it inside the product,
 *   which asks the support host under its service token (`/internal/{product}/kb`); the anonymous
 *   docs site cannot tell a customer from anyone else, so it points them at the product.
 * - `INTERNAL_SUPPORT` is Rigo staff. On the console hostname (prefix mode) `/bff` is routed, so
 *   staff can sign in right here and the host then reads them at that rung. A docs hostname routes
 *   only `/kb`, so there is no sign-in to offer there. Signing in here is a RigoHR handoff back to
 *   this article (ADR-0059); only a Development host, whose `/bff/user` 401 says `localLogin`, shows
 *   the password form instead.
 */
export function SignInGate({
  title,
  requiredRole,
  onSignedIn,
}: {
  title: string;
  requiredRole: string;
  onSignedIn: () => void;
}) {
  const { info, mode, paths } = useDocs();
  const location = useLocation();
  const [signingIn, setSigningIn] = useState(false);

  async function signIn() {
    try {
      await bffApi.user();
      onSignedIn(); // already signed in: read the article again at the session's rung
    } catch (failure) {
      if (failure instanceof ApiError && failure.problem?.localLogin === true) setSigningIn(true);
      else startHandoff(location);
    }
  }

  const staffOnly = requiredRole === ReaderRole.InternalSupport;
  const audience = staffOnly
    ? `the ${info.name} internal support team`
    : requiredRole === ReaderRole.CustomerAdmin
      ? `administrators of a ${info.name} workspace`
      : `signed-in ${info.name} users`;

  const canSignInHere = staffOnly && mode === "prefix";

  return (
    <div className="mx-auto max-w-[560px] px-10 py-24 text-center">
      <span
        className="mb-6 inline-flex size-16 items-center justify-center rounded-full"
        style={{
          background: "color-mix(in oklch, var(--warning) 18%, var(--background))",
          color: "var(--warning-text, var(--foreground))",
        }}
      >
        <Lock className="size-7" aria-hidden />
      </span>

      <h1 className="mb-3 text-2xl font-bold tracking-tight" style={{ color: "var(--foreground)" }}>
        Sign in to view this document
      </h1>

      <p className="mb-7 text-base leading-relaxed" style={{ color: "var(--muted-foreground)" }}>
        <strong style={{ color: "var(--foreground)" }}>{title}</strong> is limited to {audience}.{" "}
        {staffOnly
          ? "Sign in with a Rigo staff account to read it."
          : `Open it from inside ${info.name} with an authorized account.`}
      </p>

      {signingIn ? (
        <div className="mx-auto max-w-[360px]">
          <SignInForm onSignedIn={onSignedIn} />
        </div>
      ) : (
        <div className="flex justify-center gap-3">
          {canSignInHere && (
            <button
              type="button"
              onClick={() => void signIn()}
              className="flex h-10 cursor-pointer items-center rounded-md px-4 text-sm font-medium"
              style={{ background: "var(--primary)", color: "var(--primary-foreground)" }}
            >
              Sign in
            </button>
          )}
          {!staffOnly && info.appUrl && (
            <a
              href={info.appUrl}
              className="flex h-10 items-center rounded-md px-4 text-sm font-medium no-underline"
              style={{ background: "var(--primary)", color: "var(--primary-foreground)" }}
            >
              Log in to {info.name}
            </a>
          )}
          <Link
            to={paths.home}
            className="flex h-10 items-center rounded-md border px-4 text-sm font-medium no-underline"
            style={{ borderColor: "var(--border)", color: "var(--foreground)" }}
          >
            Back to docs
          </Link>
        </div>
      )}

      <p className="mt-6 text-xs" style={{ color: "var(--muted-foreground)" }}>
        Requires {READER_ROLE_LABELS[requiredRole] ?? "additional"} access.
      </p>
    </div>
  );
}
